// src/components/BlogDetails/BlogDetails.js
import React from "react";
import { useParams, Link } from "react-router-dom";
import "./BlogDetails.css";
import BlogHero from "../BlogHero";
import Footer from "../../Footer/Footer";
const blogContent = {
  "office-productivity": {
    title: "Top 10 Ways to Improve Office Productivity",
    image:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1600&q=80",
    content: `
      Enhancing office productivity requires a fine balance between employee well-being, organizational workflow strategies, and smart use of technology.
      Modern workplaces thrive when teams are empowered with the right tools, the right environment, and clearly defined objectives. A productive office is not
      built overnight — it is developed through consistent improvement in communication, structure, and operational clarity.

      One of the most effective ways to increase productivity is to minimize workplace distractions. Encouraging designated focus hours, reducing unnecessary
      meetings, and promoting mindful work habits can significantly improve output. Additionally, fostering a positive company culture where employees feel
      valued leads to higher motivation and responsibility toward their roles.

      Streamlined workflows also play a major role. Businesses should utilize digital project management tools, build automated systems for repetitive tasks,
      and create standard operating procedures for all major processes. When responsibilities are clear and tasks are structured properly, productivity
      naturally improves. Companies that invest in training programs and upskilling initiatives ultimately see better performance from their teams.

      Regular feedback cycles, healthy work-life balance, and comfortable workplace ergonomics contribute further to sustained productivity. When employees
      feel mentally and physically supported, their capacity to contribute effectively increases. With consistent improvements in people management and strong
      process optimization, organizations can experience remarkable gains in productivity.
    `,
  },

  "corporate-housekeeping": {
    title: "Why Corporate Housekeeping Matters in 2025",
    image:
      "https://images.unsplash.com/photo-1520880867055-1e30d1cb001c?auto=format&fit=crop&w=1600&q=80",
    content: `
      Corporate housekeeping in 2025 is not merely about cleanliness; it has evolved into a strategic pillar that influences brand reputation, workplace
      hygiene, employee health, and visitor experience. As organizations continue adapting to high-demand global standards, corporate housekeeping has become
      essential for maintaining professionalism and operational efficiency.

      A clean and well-organized workplace enhances employee morale by creating a safer and healthier atmosphere. Employees are more productive when
      the environment is free from clutter, dust, and contamination. With the growing awareness of hygiene standards, businesses are investing in
      advanced cleaning technologies, sustainable cleaning materials, and certified professional housekeeping teams.

      Corporate visitors, clients, and business partners often make subconscious judgments based on the physical condition of a workplace. A spotless
      corporate space sends a message of discipline, attention to detail, and reliability. On the other hand, a poorly maintained office can negatively
      impact a brand’s credibility. A well-managed housekeeping strategy also ensures compliance with sanitation regulations and safety policies.

      In 2025, hygiene standards have grown stricter due to increased global health awareness. Companies now implement scheduled deep-cleaning cycles,
      sanitization checkpoints, and real-time monitoring of high-traffic zones. As businesses continue to expand, professional housekeeping has become
      a key component of employee retention, brand identity, and corporate sustainability.
    `,
  },

  "industrial-cleaning-safety": {
    title: "How Industrial Cleaning Ensures Workplace Safety",
    image:
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1600&q=80",
    content: `
      Industrial cleaning is a critical factor in preventing workplace accidents and ensuring compliance with safety protocols. Facilities such as factories,
      warehouses, and production plants encounter high levels of dust, grease, chemicals, and heavy debris—making professional cleaning essential to avoid
      hazardous situations. Proper industrial cleaning minimizes the risk of slip-and-fall accidents, chemical exposure, respiratory issues, and equipment
      malfunction.

      Workers benefit greatly from a well-maintained industrial environment. Clean machinery functions more efficiently, reducing the chances of breakdowns
      and operational downtime. Industrial cleaning experts use high-grade equipment and specialized cleaning solutions to handle sensitive areas, such
      as electrical panels, heavy machinery, and ventilation systems.

      Safety compliance regulations mandate regular industrial cleaning to protect workers and equipment. Ignoring cleanliness can result in legal penalties,
      workplace injuries, and significant financial losses. A safe, clean industrial facility also boosts employee morale and fosters trust in the
      organization’s commitment to worker well-being. As industries expand, investing in a comprehensive industrial cleaning plan becomes vital for
      long-term safety and productivity.
    `,
  },

  "workplace-sanitization-tips": {
    title: "Effective Workplace Sanitization Tips for 2025",
    image:
      "https://images.unsplash.com/photo-1584982751601-97dcc096659c?auto=format&fit=crop&w=1600&q=80",
    content: `
      Workplace sanitization in 2025 has transformed into a routine priority for businesses across all sectors. Maintaining a consistently sanitized
      environment protects employees from infections, enhances workplace hygiene, and ensures a healthier work culture. With new technologies entering
      the market, organizations can now automate large parts of their sanitization process.

      High-touch surface cleaning has become a core aspect of daily sanitization routines. Items like doorknobs, lift buttons, shared desks, printer
      panels, and breakroom equipment need frequent disinfecting. Businesses should implement scheduled sanitization intervals to ensure consistency.

      Air sanitization is another critical factor. Using air purifiers, HVAC UV filters, and natural ventilation helps reduce airborne contamination.
      Sanitization stations near entry points encourage employees and visitors to follow hygiene protocols. Modern workplaces also create awareness
      programs educating employees about personal hygiene practices, making sanitization a collective responsibility.

      With the right combination of technology, training, and regular cleaning schedules, companies can maintain a safe and sanitized environment without
      disrupting workflow. This approach ensures peace of mind, improved health outcomes, and a stronger hygienic workplace culture.
    `,
  },

  "outsourcing-facility-management": {
    title: "5 Key Benefits of Outsourcing Facility Management",
    image:
      "https://images.unsplash.com/photo-1521791055366-0d553872125f?auto=format&fit=crop&w=1600&q=80",
    content: `
      Outsourcing facility management allows businesses to focus on core operations while experts handle essential services such as housekeeping, security,
      technical maintenance, and waste management. This model is becoming widely adopted as organizations seek efficiency, cost reduction, and higher
      operational accuracy.

      The first major benefit is cost savings. Outsourcing eliminates the need for hiring permanent staff, purchasing specialized equipment, or managing
      complex operational tasks internally. Facility management companies bring expertise and standardized processes that reduce unnecessary expenses.

      Another advantage is improved service quality. Professional facility teams come with years of experience, industry certifications, and access to
      modern tools that ensure superior performance. Businesses also gain flexibility, as outsourced teams can scale services up or down based on need.

      Outsourcing reduces management overhead since all daily operations, employee training, scheduling, and quality monitoring are handled externally.
      This allows companies to concentrate on strategic initiatives without worrying about facility operations.

      Additionally, outsourcing ensures compliance with legal and safety standards, reducing risks and increasing workplace safety. It is a strategic
      investment that supports business growth, reliability, and long-term sustainability.
    `,
  },

  "deep-cleaning-employee-health": {
    title: "How Deep Cleaning Improves Employee Health",
    image:
      "https://images.unsplash.com/photo-1758273705712-857d29edbde1?w=600&auto=format&fit=crop&q=60",
    content: `
      Deep cleaning goes far beyond routine dusting and sweeping—it removes hidden contaminants, kills harmful bacteria, and maintains a healthy office
      ecosystem. When workplaces invest in deep cleaning, employees experience fewer infections, respiratory problems, and allergy triggers.

      Carpets, ventilation systems, storage areas, and washrooms accumulate dirt over time. These hidden spaces foster germs that can circulate throughout
      the office. Deep cleaning ensures these areas are thoroughly sanitized using high-grade equipment and disinfectants designed to eliminate microbial
      buildup.

      Cleaner workspaces lead to better mental clarity, reduced stress, and improved employee morale. Studies show that employees working in clean
      environments take fewer sick days, resulting in higher productivity and consistent workflow. Deep cleaning also helps maintain equipment hygiene,
      ensuring devices operate efficiently.

      By prioritizing deep cleaning, companies demonstrate responsibility and care toward their employees’ well-being. Healthy employees contribute more
      actively, feel valued, and show greater loyalty to the organization.
    `,
  },

  "modern-office-security": {
    title: "Modern Security Solutions for Large Offices",
    image:
      "https://images.unsplash.com/photo-1629787155650-9ce3697dcb38?auto=format&fit=crop&w=1600&q=80",
    content: `
      Large offices today require advanced security solutions to protect employees, assets, and confidential information. Traditional security systems are
      no longer sufficient in an era where digital threats and physical breaches are becoming increasingly sophisticated.

      Modern security setups include AI-powered surveillance cameras capable of real-time threat detection, intrusion alerts, and facial recognition. These
      systems provide continuous monitoring and automatic reporting, reducing the dependency on manual security checks.

      Smart access control has also become essential. Biometric systems, RFID cards, and mobile-based entry provide secure and efficient authentication
      methods. These access systems help track who enters and exits the premises, improving accountability.

      Cybersecurity integration plays a vital role as well. Secure networks, firewall protection, and encrypted communication systems ensure data privacy
      within large organizations. Additionally, emergency response systems, panic alarms, and automated evacuation plans enhance employee safety.

      Adopting modern security technologies offers a comprehensive safety net and helps organizations maintain a secure and controlled environment.
    `,
  },

  "smart-cleaning-technology": {
    title: "The Future of Smart Cleaning Technology",
    image:
      "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=1600&q=80",
    content: `
      Smart cleaning technology marks a revolutionary shift in the facility management industry. Automated robots, IoT-enabled devices, and AI-driven cleaning
      systems have transformed the way organizations maintain hygiene standards.

      Robotic vacuum cleaners, autonomous floor scrubbers, and UV disinfection robots are becoming common in corporate and industrial spaces. These devices
      operate with precision, reduce human effort, and deliver consistent cleaning across large areas.

      IoT sensors can detect dirt levels, monitor air quality, and track washroom supply usage in real-time. This data helps cleaning teams plan activities
      more efficiently, ensuring timely sanitization without unnecessary manpower wastage.

      Smart cleaning solutions are also energy-efficient and environmentally conscious. They use optimized cleaning patterns, reduce chemical usage, and
      minimize water consumption. As technology continues to evolve, workplaces will experience faster, smarter, and more accurate cleaning outcomes.

      The future of smart cleaning technology is a blend of automation, intelligence, and sustainability—all working together to create healthier and more
      efficient workplaces.
    `,
  },

  "corporate-image-housekeeping": {
    title: "How Good Housekeeping Improves Corporate Image",
    image:
      "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1600&q=80",
    content: `
      A clean and well-maintained office leaves a lasting impression on clients, customers, and employees alike. Good housekeeping reflects a company's
      professionalism, attention to detail, and commitment to providing a healthy workplace environment.

      When clients walk into a clean office, it signals trust and reliability. Companies that maintain high housekeeping standards are viewed as organized
      and responsible. This directly contributes to building a strong corporate image in the market.

      Employees feel more valued in a clean environment, which boosts morale and encourages better performance. A tidy workspace reduces stress and
      enhances workflow efficiency. Regular housekeeping ensures that employees stay focused, comfortable, and motivated.

      Housekeeping also plays a significant role in maintaining workplace safety. Proper waste disposal, organized storage systems, and hazard-free
      work areas prevent accidents. These improvements not only benefit employees but also strengthen the company's reputation as a safe and caring employer.
    `,
  },

  "eco-friendly-cleaning": {
    title: "Eco-Friendly Cleaning Solutions for Workplaces",
    image:
      "https://plus.unsplash.com/premium_photo-1683141112334-d7d404f6e716?w=600&auto=format&fit=crop&q=60",
    content: `
      Eco-friendly cleaning is no longer a trend—it is a necessity. Workplaces are adopting green cleaning practices to protect employee health, reduce
      environmental impact, and support sustainability efforts. Green cleaning products avoid harsh chemicals, making them safer for both people and the
      planet.

      Natural cleaning agents like vinegar, citrus extracts, and plant-based solutions effectively remove dirt while reducing toxins in the air. Businesses
      that use eco-friendly products contribute to cleaner indoor air quality and prevent harmful residues from accumulating on surfaces.

      Eco-friendly cleaning practices also involve using energy-efficient tools, minimizing water consumption, and reducing plastic waste. These
      sustainable methods create a healthier workspace while aligning companies with global environmental goals.

      Employees appreciate organizations that prioritize eco-conscious choices, improving brand loyalty and corporate reputation. Eco-friendly cleaning
      is a long-term investment that benefits people, businesses, and the environment simultaneously.
    `,
  },

  "office-cleaning-mistakes": {
    title: "Top Mistakes Offices Make in Daily Cleaning",
    image:
      "https://images.unsplash.com/photo-1559209537-dafe2fe2886b?w=600&auto=format&fit=crop&q=60",
    content: `
      Many offices unknowingly make cleaning mistakes that reduce hygiene levels and impact employee health. One common error is ignoring high-touch
      surfaces like keyboards, phones, desk handles, and light switches. These areas accumulate the most germs but are often overlooked.

      Another frequent mistake is using the wrong cleaning products. Harsh chemicals may damage surfaces or create harmful odors that cause discomfort to
      employees. Using improper tools or outdated techniques can also leave spaces partially cleaned.

      Offices also tend to neglect deep cleaning activities, such as carpet shampooing, upholstery cleaning, and ventilation system maintenance. Dust,
      mold, and allergens trapped in these areas can circulate throughout the office, triggering respiratory issues.

      Inconsistent cleaning schedules further reduce workplace hygiene. Businesses should follow structured cleaning routines that include daily wiping,
      weekly deep cleaning, and monthly sanitization cycles.

      Avoiding these mistakes helps maintain a cleaner, healthier, and more productive workplace environment.
    `,
  },

  "professional-pantry-management": {
    title: "How Professional Pantry Management Improves Productivity",
    image:
      "https://images.unsplash.com/photo-1682071308392-2e7a285fc11c?w=600&auto=format&fit=crop&q=60",
    content: `
      Professional pantry management significantly contributes to workplace efficiency and employee satisfaction. A well-organized pantry ensures that
      employees have quick access to beverages, snacks, and essentials without wasting valuable time. Managed pantries also provide consistency in
      cleanliness, stock availability, and service quality.

      Professional teams ensure timely restocking, proper storage of supplies, and hygienic preparation of refreshments. A clean pantry encourages
      employees to take short and refreshing breaks, which helps improve productivity and mental energy.

      A well-managed pantry also reflects company culture. Employees feel appreciated when they see that the company invests in high-quality pantry
      services. This improves morale, reduces stress, and encourages teamwork. In larger organizations, a well-run pantry helps streamline breakroom
      flow, eliminating unnecessary crowding or delays.

      Pantry management also optimizes supply consumption, reduces wastage, and ensures quality control in every item served. With structured processes,
      companies achieve both efficiency and employee satisfaction.
    `,
  },
};



export default function BlogDetails() {
  const { slug } = useParams();

  // First check hardcoded blogs
  let blog = blogContent[slug];

  // If not found in hardcoded, check admin blogs from localStorage
  if (!blog) {
    const storedBlogs = localStorage.getItem('adminBlogs');
    if (storedBlogs) {
      const adminBlogs = JSON.parse(storedBlogs);
      const adminBlog = adminBlogs.find(b => {
        const blogSlug = b.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        return blogSlug === slug;
      });

      if (adminBlog) {
        blog = {
          title: adminBlog.title,
          image: adminBlog.image || "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1600&q=80",
          content: adminBlog.content
        };
      }
    }
  }

  if (!blog) return <h2>Blog Not Found</h2>;

  return (
   <div>
  <BlogHero />

  <div className="blog-details-wrapper">
    <div className="blog-details-header">
      <img src={blog.image} alt={blog.title} />
      <h1>{blog.title}</h1>
    </div>

    <div className="blog-details-content">
      <p>{blog.content}</p>

      {/* Updated button className to match CSS */}
      <Link to="/blogs" className="btn">← Back to Blogs</Link>
    </div>
  </div>

  <Footer />
</div>

  );
}
